'use client';
import { usePathname } from 'next/navigation';
import { alpha, Box, Button, Checkbox, FormControlLabel, useTheme } from '@mui/material';

import { ComponentErrorBoundary, FilterDropdown } from '@/components';
import { useT } from '@/hooks';

import {
  FREQUENCY_FILTER_OPTIONS,
  SORT_FILTER_OPTIONS,
  STATUS_FILTER_OPTIONS,
} from '@/constants/filters.constants';

interface MarketFiltersProps {
  sortBy: string;
  frequency: string;
  status: string;
  hideSport?: boolean;
  onSortByChange: (value: string) => void;
  onFrequencyChange: (value: string) => void;
  onStatusChange: (value: string) => void;
  onHideSportChange?: (value: boolean) => void;
  onClearFilters: () => void;
}

const HighlightOffIcon = ({ sx }: { sx?: any }) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={sx}
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

export const MarketFilters = ({
  sortBy,
  frequency,
  status,
  hideSport = false,
  onSortByChange,
  onFrequencyChange,
  onStatusChange,
  onHideSportChange,
  onClearFilters,
}: MarketFiltersProps) => {
  const { t } = useT();
  const theme = useTheme();
  const pathname = usePathname();
  const isHomePage = pathname === '/';

  return (
    <ComponentErrorBoundary componentName="Market Filters">
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          pb: 0.5,
          alignItems: 'center',
          '&::-webkit-scrollbar': { display: 'none' },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <FilterDropdown
          label="Sort by"
          value={sortBy}
          options={SORT_FILTER_OPTIONS}
          onChange={onSortByChange}
          minWidth={140}
        />
        <FilterDropdown
          label="Frequency"
          value={frequency}
          options={FREQUENCY_FILTER_OPTIONS}
          onChange={onFrequencyChange}
          minWidth={120}
        />
        <FilterDropdown
          label="Status"
          value={status}
          options={STATUS_FILTER_OPTIONS}
          onChange={onStatusChange}
          minWidth={110}
        />

        {isHomePage && (
          <FormControlLabel
            control={
              <Checkbox
                checked={hideSport}
                onChange={(e) => onHideSportChange?.(e.target.checked)}
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&.Mui-checked': {
                    color: 'primary.main',
                  },
                  padding: '4px',
                }}
              />
            }
            label={t('filter.Hide sports')}
            sx={{
              ml: 1,
              mr: 1,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'text.secondary',
                whiteSpace: 'nowrap',
              },
            }}
          />
        )}

        <Button
          startIcon={<HighlightOffIcon sx={{ fontSize: '1rem !important' }} />}
          onClick={onClearFilters}
          sx={{
            textTransform: 'none',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'text.secondary',
            minWidth: 'fit-content',
            whiteSpace: 'nowrap',
            borderRadius: '16px',
            px: 1.2,
            height: 32,
            border: '1px solid',
            borderColor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.common.white, 0.08)
                : alpha(theme.palette.common.black, 0.08),
            bgcolor: alpha(theme.palette.text.primary, 0.02),
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            gap: 0.5, // Reduced gap between icon and text
            '& .MuiButton-startIcon': {
              marginRight: '0 !important',
              marginLeft: '0 !important',
            },
            '&:hover': {
              color: 'error.main',
              borderColor: alpha(theme.palette.error.main, 0.2),
              bgcolor: alpha(theme.palette.error.main, 0.04),
              transform: 'translateY(-1px)',
              boxShadow: `0 2px 6px ${alpha(theme.palette.error.main, 0.1)}`,
            },
            '&:active': {
              transform: 'translateY(0)',
            },
          }}
        >
          {t('filter.Clear filters')}
        </Button>
      </Box>
    </ComponentErrorBoundary>
  );
};
